/**
 * Requirement record schema — DataTable columns + FormModal fields
 * for the Requirements half of Step 8 (`cybersecurity_concept_agent`,
 * per ISO 21434 RQ-09-09). Requirements implement controls (the other
 * half of Step 8); `control_id` is therefore a SELF-REFERENCE within
 * Step 8 rather than a cross-step FK.
 *
 * Real DB shape:
 *   { req_id (PK), req_title, req_statement, req_rationale,
 *     requirement_type ('item' | 'operational_environment'),
 *     control_id, cal_level ('CAL1' | 'CAL2' | 'CAL3' | 'CAL4'),
 *     // hierarchical: source, source_session_id, is_relevant,
 *     // relevance_justification, affected_components }
 */
import React from 'react';
import LevelBadge from '../../../../components/ui/LevelBadge';
import CategoryPill from '../../../../components/ui/CategoryPill';
import { getCALColor } from '../../../../utils/severityStyle';

export interface RequirementRecord {
  req_id: string;
  req_title?: string;
  req_statement?: string;
  req_rationale?: string;
  requirement_type?: string;
  control_id?: string;
  cal_level?: string;
  source?: string;
  source_session_id?: string;
  is_relevant?: boolean;
  relevance_justification?: string;
  [key: string]: unknown;
}

export const REQUIREMENT_TYPES = [
  { value: 'item', label: 'Item' },
  { value: 'operational_environment', label: 'Operational Environment' },
];

export const CAL_LEVELS = [
  { value: 'CAL1', label: 'CAL1' },
  { value: 'CAL2', label: 'CAL2' },
  { value: 'CAL3', label: 'CAL3' },
  { value: 'CAL4', label: 'CAL4' },
];

const REQUIREMENT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  REQUIREMENT_TYPES.map((t) => [t.value, t.label]),
);

const renderType = (row: RequirementRecord) =>
  row.requirement_type ? (
    <CategoryPill
      label={REQUIREMENT_TYPE_LABELS[row.requirement_type] ?? String(row.requirement_type)}
      size="sm"
    />
  ) : (
    ''
  );

const renderCal = (row: RequirementRecord) =>
  row.cal_level ? (
    <LevelBadge
      color={getCALColor(row.cal_level)}
      label={String(row.cal_level)}
      size="sm"
    />
  ) : (
    ''
  );

export const REQUIREMENT_COLUMNS_READ = [
  { key: 'req_id', label: 'ID', sortable: true, width: '7%' },
  { key: 'req_title', label: 'Title', sortable: true, width: '17%' },
  { key: 'req_statement', label: 'Statement', sortable: false, width: '26%' },
  { key: 'req_rationale', label: 'Rationale', sortable: false, width: '22%' },
  { key: 'requirement_type', label: 'Type', sortable: true, width: '12%', render: renderType },
  { key: 'control_id', label: 'Control', sortable: true, width: '9%' },
  { key: 'cal_level', label: 'CAL', sortable: true, width: '7%', render: renderCal },
];

export const REQUIREMENT_COLUMNS_EDIT = [
  { key: 'req_id', label: 'ID', sortable: true, width: '6%' },
  { key: 'req_title', label: 'Title', sortable: true, width: '16%' },
  { key: 'req_statement', label: 'Statement', sortable: false, width: '26%' },
  { key: 'req_rationale', label: 'Rationale', sortable: false, width: '22%' },
  { key: 'requirement_type', label: 'Type', sortable: true, width: '12%', render: renderType },
  { key: 'control_id', label: 'Control', sortable: true, width: '9%' },
  { key: 'cal_level', label: 'CAL', sortable: true, width: '7%', render: renderCal },
];

export const REQUIREMENT_ACTIONS_COLUMN_WIDTH = '4%';

const REQUIREMENT_ID_PATTERN = /^[A-Z0-9_-]+$/;

export const REQUIREMENT_FORM_FIELDS = [
  {
    name: 'req_id',
    label: 'Requirement ID',
    type: 'text',
    required: true,
    autoFocus: true,
    placeholder: 'e.g. REQ-01',
    helpText: 'Unique identifier (per ISO 21434 RQ-09-09).',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return 'Requirement ID is required';
      if (!REQUIREMENT_ID_PATTERN.test(s)) {
        return 'Requirement ID must contain only uppercase letters, numbers, underscores, and hyphens';
      }
      return null;
    },
    width: 'half',
  },
  {
    name: 'cal_level',
    label: 'CAL Level',
    type: 'select',
    required: true,
    options: CAL_LEVELS,
    helpText: 'Cybersecurity Assurance Level inherited from the goal.',
    width: 'half',
  },
  {
    name: 'control_id',
    label: 'Parent Control',
    type: 'select',
    required: true,
    options: [] as Array<{ value: string; label: string }>,
    helpText: 'The control this requirement implements.',
    width: 'half',
  },
  {
    name: 'requirement_type',
    label: 'Requirement Type',
    type: 'select',
    required: true,
    options: REQUIREMENT_TYPES,
    helpText:
      'Item = realised by the item under analysis. Operational Environment = realised by external systems.',
    width: 'half',
  },
  {
    name: 'req_title',
    label: 'Requirement Title',
    type: 'text',
    required: true,
    placeholder: 'Short title.',
  },
  {
    name: 'req_statement',
    label: 'Requirement Statement',
    type: 'textarea',
    required: true,
    rows: 3,
    placeholder: 'The ECU SHALL …',
    helpText: 'Formal SHALL statement per ISO 21434 RQ-09-09.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 20) return 'Statement must be at least 20 characters.';
      return null;
    },
  },
  {
    name: 'req_rationale',
    label: 'Rationale',
    type: 'textarea',
    required: true,
    rows: 2,
    placeholder: 'Why this requirement is necessary.',
  },
];
